/**
 * Record search for the command palette: a handful of properties, deals and
 * contacts whose name matches what was typed.
 *
 * Reads only. Every query runs on the caller's own session, so RLS decides
 * what comes back exactly as it does on the list pages — the palette can never
 * show a row its list page would not.
 */
import { supabase } from '@/lib/supabase';
import { ilikeAny, toIlikePattern } from './globalSearchQuery';

export type SearchHitType = 'property' | 'deal' | 'contact';

export interface SearchHit {
  id: string;
  type: SearchHitType;
  title: string;
  subtitle: string | null;
  href: string;
}

export interface GlobalSearchResults {
  properties: SearchHit[];
  deals: SearchHit[];
  contacts: SearchHit[];
}

export const EMPTY_SEARCH_RESULTS: GlobalSearchResults = {
  properties: [],
  deals: [],
  contacts: [],
};

/** Rows per group. The palette is for jumping, not browsing. */
const LIMIT = 5;

const joined = (parts: Array<string | null | undefined>, separator: string): string | null => {
  const present = parts.map((p) => p?.trim()).filter((p): p is string => !!p);
  return present.length > 0 ? present.join(separator) : null;
};

async function searchProperties(pattern: string): Promise<SearchHit[]> {
  const { data, error } = await supabase
    .from('properties')
    .select('id, name, address_street, address_city, address_state')
    .is('deleted_at', null)
    .or(ilikeAny(['name', 'address_street', 'address_city'], pattern))
    .order('name', { ascending: true })
    .limit(LIMIT);
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    type: 'property' as const,
    title: row.name || row.address_street || 'Unnamed property',
    subtitle: joined([row.address_city, row.address_state], ', '),
    href: `/properties/${row.id}`,
  }));
}

async function searchDeals(pattern: string): Promise<SearchHit[]> {
  const { data, error } = await supabase
    .from('deals')
    .select('id, name, status')
    .or(ilikeAny(['name'], pattern))
    .order('created_at', { ascending: false })
    .limit(LIMIT);
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    type: 'deal' as const,
    title: row.name || 'Untitled deal',
    subtitle: typeof row.status === 'string' ? row.status.replace(/_/g, ' ') : null,
    href: `/deals/${row.id}`,
  }));
}

async function searchContacts(pattern: string): Promise<SearchHit[]> {
  const { data, error } = await supabase
    .from('contacts')
    .select('id, name, company, email')
    .or(ilikeAny(['name', 'company', 'email'], pattern))
    .order('name', { ascending: true })
    .limit(LIMIT);
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    type: 'contact' as const,
    title: row.name || row.email || 'Unnamed contact',
    subtitle: joined([row.company, row.email], ' · '),
    href: `/contacts/${row.id}`,
  }));
}

/**
 * Search all three record types at once. One group failing (a column renamed,
 * a permission tightened) empties that group and leaves the others standing:
 * a partial answer is more use in a palette than an error.
 */
export async function globalSearch(query: string): Promise<GlobalSearchResults> {
  const pattern = toIlikePattern(query);
  if (!pattern) return EMPTY_SEARCH_RESULTS;

  const [properties, deals, contacts] = await Promise.allSettled([
    searchProperties(pattern),
    searchDeals(pattern),
    searchContacts(pattern),
  ]);

  const settled = (result: PromiseSettledResult<SearchHit[]>, label: string): SearchHit[] => {
    if (result.status === 'fulfilled') return result.value;
    console.warn(`[globalSearch] ${label} search failed:`, result.reason);
    return [];
  };

  return {
    properties: settled(properties, 'property'),
    deals: settled(deals, 'deal'),
    contacts: settled(contacts, 'contact'),
  };
}
